import { apiClient } from './api';
import type { FarmerFormData, GetFarmersResponse, GetFarmerResponse, CreateFarmerResponse, UpdateFarmerResponse, FarmerDocument } from '../types/farmer';

export const farmerService = {
  async getFarmers(options?: {
    page?: number;
    limit?: number;
    search?: string;
    county?: string;
    crop?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    adminCode?: string;
    adminLevel?: string;
    organizationId?: string;
    isActive?: boolean;
    viewAllOrgs?: boolean;
  }): Promise<GetFarmersResponse> {
    try {
      const response = await apiClient.getFarmers(options);
      return response;
    } catch (error) {
      console.error('Error fetching farmers:', error);
      throw error;
    }
  },

  async getFarmerById(id: string): Promise<GetFarmerResponse> {
    try {
      const response = await apiClient.getFarmerById(id);
      return response;
    } catch (error) {
      console.error('Error fetching farmer:', error);
      throw error;
    }
  },

  async createFarmer(farmerData: FarmerFormData): Promise<CreateFarmerResponse> {
    try {
      const response = await apiClient.createFarmer(farmerData);
      return response;
    } catch (error) {
      console.error('Error creating farmer:', error);
      throw error;
    }
  },

  async updateFarmer(id: string, farmerData: Partial<FarmerFormData>): Promise<UpdateFarmerResponse> {
    try {
      const response = await apiClient.updateFarmer(id, farmerData);
      return response;
    } catch (error) {
      console.error('Error updating farmer:', error);
      throw error;
    }
  },

  async deleteFarmer(id: string, hardDelete: boolean = false): Promise<{ message: string }> {
    try {
      const response = await apiClient.deleteFarmer(id, hardDelete);
      return response;
    } catch (error) {
      console.error('Error deleting farmer:', error);
      throw error;
    }
  },

  async uploadDocument(file: File, documentType: string, farmerId?: string): Promise<{ document: FarmerDocument }> {
    try {
      const response = await apiClient.uploadFarmerDocument(file, documentType, farmerId);
      return response;
    } catch (error) {
      console.error('Error uploading farmer document:', error);
      throw error;
    }
  },

  async deleteDocument(farmerId: string, documentId: string): Promise<void> {
    try {
      await apiClient.deleteFarmerDocument(farmerId, documentId);
    } catch (error) {
      console.error('Error deleting farmer document:', error);
      throw error;
    }
  },

  async validateFarmerGeolocation(farmerId: string): Promise<{
    farmerId: string;
    farmerName: string;
    status: string;
    details: Record<string, unknown>;
  }> {
    const token = localStorage.getItem('auth-token');
    const response = await fetch(`/api/farmers/${farmerId}/validate-geolocation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as Record<string, string>).message || 'Geolocation validation failed');
    }
    const result = await response.json();
    return result.data;
  },

  async validateAllFarmerGeolocations(): Promise<{
    total: number;
    valid: number;
    invalid: number;
    noCoordinates: number;
    noAdminUnit: number;
  }> {
    const token = localStorage.getItem('auth-token');
    const response = await fetch('/api/farmers/validate-geolocations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as Record<string, string>).message || 'Batch geolocation validation failed');
    }
    const result = await response.json();
    return result.data;
  },

  async getGeoBoundaryStatus(): Promise<{
    totalUnits: number;
    unitsWithGeometry: number;
    coveragePercent: number;
    byLevel: Record<number, { total: number; withGeometry: number }>;
  }> {
    const token = localStorage.getItem('auth-token');
    const response = await fetch('/api/admin/geo-boundaries/status', {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as Record<string, string>).message || 'Failed to get boundary status');
    }
    const result = await response.json();
    return result.data;
  },

  async getAdminBoundaryGeometry(adminCode: string): Promise<{
    adminCode: string;
    adminName: string;
    adminLevel: number;
    geometry: GeoJSON.Geometry;
  } | null> {
    const token = localStorage.getItem('auth-token');
    const response = await fetch(`/api/admin/geo-boundaries/${encodeURIComponent(adminCode)}/geometry`, {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) {
      if (response.status === 404) return null;
      const err = await response.json().catch(() => ({}));
      throw new Error((err as Record<string, string>).message || 'Failed to get boundary geometry');
    }
    const result = await response.json();
    return result.data;
  },

  async downloadBulkUploadTemplate(): Promise<void> {
    try {
      const { blob, filename } = await apiClient.downloadFarmerBulkUploadTemplate();
      
      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'farmer_bulk_upload_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading bulk upload template:', error);
      throw error;
    }
  },
};